import { Check, Clock, Plus } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { useInbox } from "@/hooks/useInbox";
import { useLeadCycleStats } from "@/hooks/useLeads";
import { useBudgets, useBudgetStats } from "@/hooks/useBudgets";
import { useInstallments } from "@/hooks/usePayments";
import { useAdminProfile, firstNameOf } from "@/hooks/useAdminProfile";
import { CockpitQueue } from "./CockpitQueue";
import { BudgetComposerPreview } from "./BudgetComposerPreview";
import { QuickCreateLeadDialog } from "./QuickCreateLeadDialog";
import { effectiveInstallmentStatus } from "@/lib/installmentStatus";
import { formatBRL } from "@/lib/format";

const fmt = (v: number) => formatBRL(v);

function greetingFor(date: Date) {
  const h = date.getHours();
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Loading placeholder for a stat card's headline/subtitle line — avoids
 * flashing "R$ 0,00" / "0 dias" before the real query result lands. */
function StatSkeleton({
  w,
  h = "h-[1em]",
  light = false,
}: {
  w: string;
  h?: string;
  light?: boolean;
}) {
  return (
    <span
      className={`inline-block ${h} animate-pulse rounded-md align-middle ${w} ${
        light ? "bg-panel-foreground/25" : "bg-muted"
      }`}
      aria-hidden="true"
    />
  );
}

export function CommandInbox() {
  const { data: inbox, isLoading: inboxLoading } = useInbox();
  const { data: budgetStats, isLoading: budgetStatsLoading } = useBudgetStats();
  const { data: negotiatingBudgets, isLoading: negotiatingLoading } = useBudgets({
    status: "sent",
  });
  const { data: acceptedBudgets } = useBudgets({ status: "accepted" });
  const { data: cycleStats, isLoading: cycleLoading } = useLeadCycleStats();
  const { data: installments, isLoading: installmentsLoading } = useInstallments();
  const { data: profile } = useAdminProfile();

  const pending = inbox?.length ?? 0;

  // Card 1 — "Em negociação": budgets sent to a client and awaiting a
  // decision (real figure, from useBudgets({status:"sent"})).
  const negotiationTotal = (negotiatingBudgets ?? []).reduce((s, b) => s + Number(b.net_total), 0);
  const negotiationCount = negotiatingBudgets?.length ?? 0;

  // Card 2 — "Fechado no mês": budgetStats.monthlyRevenue is the existing,
  // already-computed figure (accepted budgets created this month). The
  // sparkline underneath is a real trend built from the accepted-budget
  // amounts closed so far this month — not decorative fake data — and is
  // simply omitted when there isn't at least one to show.
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const sparkValues = (acceptedBudgets ?? [])
    .filter((b) => new Date(b.created_at) >= startOfMonth)
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    .slice(-10)
    .map((b) => Number(b.net_total));
  const sparkMax = Math.max(1, ...sparkValues);

  // Card 3 — "Tempo médio de ciclo": derived from real lead created_at ->
  // converted_at gaps (useLeadCycleStats, new additive hook in useLeads.ts).
  const avgDays = cycleStats?.avgDays ?? null;
  const deltaDays = cycleStats?.deltaVsLastMonth ?? null;

  // Card 4 — "A receber": real pending/overdue payment installments due on
  // or before the end of the current month (useInstallments from
  // usePayments.ts — the same table FinanceDashboard's "receivable" figure
  // is built from).
  const monthLabel = format(now, "MMMM", { locale: ptBR });
  const todayLabel = capitalize(format(now, "EEEE, d 'de' MMMM", { locale: ptBR }));
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  const receivableThisMonth = (installments ?? []).filter(
    (i) =>
      (i.status === "pending" || i.status === "overdue") &&
      i.due_date &&
      new Date(i.due_date) <= endOfMonth,
  );
  const receivableTotal = receivableThisMonth.reduce((s, i) => s + Number(i.amount), 0);
  const onTimeCount = receivableThisMonth.filter(
    (i) => effectiveInstallmentStatus(i) === "pending",
  ).length;
  const overdueCount = receivableThisMonth.filter(
    (i) => effectiveInstallmentStatus(i) === "overdue",
  ).length;

  return (
    <div className="mx-auto flex max-w-[1320px] flex-col gap-6">
      <header className="flex flex-wrap items-end justify-between gap-5">
        <div>
          <h1 className="mb-1.5 font-display text-[29px] font-bold tracking-[-0.03em]">
            {greetingFor(now)}, {firstNameOf(profile?.name)}
          </h1>
          <p className="text-sm text-muted-foreground">
            {todayLabel} ·{" "}
            <strong className="font-semibold text-foreground">
              {pending} {pending === 1 ? "item" : "itens"}
            </strong>{" "}
            {pending === 1 ? "pede" : "pedem"} decisão hoje.
          </p>
        </div>
        <QuickCreateLeadDialog
          trigger={
            <Button className="h-[38px] gap-[7px] rounded-[10px] px-4 text-[13px] font-semibold">
              <Plus className="h-[15px] w-[15px]" strokeWidth={2.2} />
              Novo lead
            </Button>
          }
        />
      </header>

      {/* Stat cards */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(215px,1fr))] gap-4">
        <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Em negociação</span>
            <span className="flex h-[26px] w-[26px] items-center justify-center rounded-lg bg-warn-soft text-warn">
              <Clock className="h-3.5 w-3.5" />
            </span>
          </div>
          <div className="mt-3 font-display text-2xl font-bold tracking-tight">
            {negotiatingLoading ? <StatSkeleton w="w-28" /> : fmt(negotiationTotal)}
          </div>
          <div className="mt-1 text-[11.5px] text-muted-foreground">
            {negotiatingLoading ? (
              <StatSkeleton w="w-32" />
            ) : (
              <>
                {negotiationCount} proposta{negotiationCount === 1 ? "" : "s"} aberta
                {negotiationCount === 1 ? "" : "s"}
              </>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Fechado no mês</span>
            <span className="flex h-[26px] w-[26px] items-center justify-center rounded-lg bg-ok-soft text-ok">
              <Check className="h-3.5 w-3.5" />
            </span>
          </div>
          <div className="mt-3 font-display text-2xl font-bold tracking-tight">
            {budgetStatsLoading ? <StatSkeleton w="w-28" /> : fmt(budgetStats?.monthlyRevenue ?? 0)}
          </div>
          {(budgetStats?.monthlyBarter ?? 0) > 0 && (
            <div className="mt-1 text-[11.5px] font-semibold text-muted-foreground">
              {fmt(budgetStats!.monthlyBarter)} em permuta
            </div>
          )}
          {sparkValues.length > 0 && (
            <div className="mt-2.5 flex h-[34px] items-end gap-1" aria-hidden="true">
              {sparkValues.map((v, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-sm bg-primary/25"
                  style={{ height: `${Math.max(12, (v / sparkMax) * 100)}%` }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
          <span className="text-xs font-medium text-muted-foreground">Tempo médio de ciclo</span>
          <div className="mt-3 flex items-baseline gap-1.5">
            {cycleLoading ? (
              <StatSkeleton w="w-12" />
            ) : (
              <>
                <span className="font-display text-2xl font-bold tracking-tight">
                  {avgDays != null ? Math.round(avgDays) : "—"}
                </span>
                <span className="text-sm text-muted-foreground">dias</span>
              </>
            )}
          </div>
          {cycleLoading ? (
            <div className="mt-1">
              <StatSkeleton w="w-36" />
            </div>
          ) : deltaDays != null ? (
            <div
              className={`mt-1 text-[11.5px] font-semibold ${deltaDays <= 0 ? "text-ok" : "text-bad"}`}
            >
              {deltaDays <= 0 ? "−" : "+"}
              {Math.abs(Math.round(deltaDays))} dias vs. mês passado
            </div>
          ) : (
            <div className="mt-1 text-[11.5px] text-muted-foreground">
              Sem comparação disponível
            </div>
          )}
        </div>

        <div className="rounded-2xl bg-primary p-5 text-primary-foreground shadow-card">
          <div className="text-xs font-medium opacity-80">A receber em {monthLabel}</div>
          <div className="mt-3 font-display text-2xl font-bold tracking-tight">
            {installmentsLoading ? <StatSkeleton w="w-28" light /> : fmt(receivableTotal)}
          </div>
          <div className="mt-3 flex gap-2">
            {installmentsLoading ? (
              <StatSkeleton w="w-full" h="h-[30px]" light />
            ) : (
              <>
                <span className="flex-1 rounded-[9px] bg-panel-foreground/[0.16] px-2.5 py-[7px] text-[11px] font-semibold">
                  {onTimeCount} no prazo
                </span>
                <span className="flex-1 rounded-[9px] bg-panel-foreground/[0.16] px-2.5 py-[7px] text-[11px] font-semibold">
                  {overdueCount} vencida{overdueCount === 1 ? "" : "s"}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Cockpit widget — dark queue + budget composer preview */}
      <div className="grid grid-cols-1 overflow-hidden rounded-[20px] bg-panel shadow-card md:grid-cols-[380px_1fr]">
        <CockpitQueue items={inbox} isLoading={inboxLoading} />
        <BudgetComposerPreview />
      </div>
    </div>
  );
}
